import { mergeWithReplaceArray } from '@shared/utils'
import { useForm } from '@tanstack/react-form'
import { zodValidator } from '@tanstack/zod-form-adapter'
import { cn } from 'lib/utils'
import { debounce, mergeWith } from 'lodash'
import { ButtonWithLoader } from 'primitives/button'
import { useEffect } from 'react'
import { ErrorBoundary } from './boundary'
import { FieldsRenderer } from './fields-renderer'
import { FormMounter } from './form-mounter'
import { useGenericFormStore } from './persistance-store'
import type { GenericFormProps, GenericFormType } from './types'

const debouncedStateUpdate = debounce((key: string, values: unknown) => {
  const store = useGenericFormStore.getState()
  store.setDefaultValues(key, values as Record<string, unknown>)
}, 500) // need to throttle this to avoid too many publish and write events

export const GenericFormCore = <T,>({
  fields,
  onSubmit,
  children,
  defaultValues,
  className,
  persistanceKey,
  id,
  includeSubmitButton = true,
  isSubmitting,
  submitText,
  onMount,
  formValidators,
  errorComponent: ErrorRenderer,
}: GenericFormProps<T>) => {
  const form = useForm<T, ReturnType<typeof zodValidator>>({
    defaultValues:
      (persistanceKey &&
        (useGenericFormStore.getState().formValues[persistanceKey || ''] as T | undefined)) ||
      defaultValues,
    onSubmit: ({ value }) => onSubmit(value),
    validatorAdapter: zodValidator(),
    validators: formValidators,
  })

  useEffect(() => {
    // Purpose: Persist form values when they change
    // This effect sets up a subscription to form value changes and
    // updates the persistent store with debounced updates
    const unsubscribe = form.store.subscribe(() => {
      if (persistanceKey) debouncedStateUpdate(persistanceKey, form.state.values as unknown)
    })
    return () => {
      unsubscribe()
    }
  }, [persistanceKey])

  useEffect(() => {
    // Purpose: Reset form when default values change
    // This ensures the form is updated if the defaultValues prop changes
    form.reset()
  }, [defaultValues, form])

  useEffect(() => {
    // Purpose: Initialize form and set up reset functionality
    // This effect runs once on mount to set up the form and provide
    // a way to clear persisted data and reset the form
    const clearAndReset = (data: Partial<T>) => {
      if (!data) useGenericFormStore.getState().clear(persistanceKey || '')
      else
        useGenericFormStore
          .getState()
          .setDefaultValues(
            persistanceKey || '',
            mergeWith({}, form.state.values, data, mergeWithReplaceArray),
          )
      form.reset()
    }
    const cleanup = onMount?.(form, clearAndReset)
    return cleanup || undefined
  }, [])

  return (
    <form
      id={id}
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className={cn('grid gap-2', className)}
    >
      <FormMounter id={id} form={form as GenericFormType<unknown>} />
      <FieldsRenderer fields={fields} form={form} />
      {ErrorRenderer && <ErrorRenderer form={form} />}
      {includeSubmitButton && (
        <form.Subscribe
          selector={(state) => state.isTouched}
          children={(isTouched) => (
            <div className="flex mt-4 col-span-full sm:grid-cols-2">
              <ButtonWithLoader
                type="submit"
                loading={isSubmitting as boolean}
                disabled={!isTouched}
                aria-disabled={!isTouched}
              >
                {submitText || 'Submit'}
              </ButtonWithLoader>
            </div>
          )}
        />
      )}
      {children}
    </form>
  )
}

export const GenericForm = <T,>(props: GenericFormProps<T>) => (
  <ErrorBoundary>
    <GenericFormCore {...props} />
  </ErrorBoundary>
)
